import { TWorkflowExecution } from '@commenda/types';
import {
  ServiceRequestDetails,
  TaskGroupWithSLA,
  TaskWithSLA,
} from '@commenda/types/src/service-request/types';
import { TaskParty, TaskStatus } from '@prisma/client';
import { KeyedMutator } from 'swr';
import { TimelineRail } from '../../../../../molecules/TimelineRail';
import { ServiceAuditorStepHelper } from '../../../../../workflow/helpers/ServiceAuditorStepHelper';
import { ServiceRequestTaskRenderer } from '../../../../common/tasks/ServiceRequestTaskRenderer';
import { ServiceAuditTimelineIndicator } from '../../../../service-audit/components/ServiceAuditTimelineIndicator';
import { ServiceRequestTaskCardDisplayVariant } from '../../../../types';
import { getTaskStatus, isCurrentTimelineTask } from '../../../../utils';
import { TaskStepIndicator } from './TaskStepIndicator';

type ServiceRequestTimelineTaskProps = {
  isFirst: boolean;
  isLast: boolean;
  serviceRequest: ServiceRequestDetails;
  taskGroup: TaskGroupWithSLA;
  role: TaskParty;
  task: TaskWithSLA;
  mutate: KeyedMutator<unknown>;
  workflowExecution?: TWorkflowExecution;
};

export const ServiceRequestTimelineTask = ({
  isFirst,
  isLast,
  serviceRequest,
  taskGroup,
  role,
  task,
  mutate,
  workflowExecution,
}: ServiceRequestTimelineTaskProps) => {
  const taskStatus = getTaskStatus(task, serviceRequest);
  const isCurrentStep =
    taskStatus === TaskStatus.TO_DO &&
    isCurrentTimelineTask({ ...task, taskGroup });
  // Auditor steps and their wait steps follow the audit, so a failed audit does not show the green tick.
  const auditorStepId = ServiceAuditorStepHelper.findAuditorStepIdForStep(
    workflowExecution?.WorkflowTemplateVersion?.definition,
    task.workflowDefinitionStepId
  );

  return (
    <div className="flex gap-0.5 pr-4">
      <TimelineRail
        className="w-10 shrink-0"
        isFirst={isFirst}
        isLast={isLast}
        indicator={
          auditorStepId ? (
            <ServiceAuditTimelineIndicator
              auditorStepId={auditorStepId}
              role={role}
              workflowExecution={workflowExecution}
              taskStatus={taskStatus}
              isCurrent={isCurrentStep}
            />
          ) : (
            <TaskStepIndicator status={taskStatus} isCurrent={isCurrentStep} />
          )
        }
      />

      <div className="flex-1 flex flex-col gap-4 min-w-0 pt-4 pb-3">
        <ServiceRequestTaskRenderer
          task={{
            ...task,
            status: taskStatus,
            taskGroup,
          }}
          context={{
            role,
            serviceRequest,
            mutate,
            variant: ServiceRequestTaskCardDisplayVariant.DEFAULT,
            workflowExecution,
          }}
          isTimelineView
          displayNumber={task.legacyServiceRequestTaskOrderNumber ?? 0}
        />
      </div>
    </div>
  );
};
